// Function index:
//   DrawerCtl                       — { choose, signal } passed to every form
//       so submit handlers can resolve the title screen or be aborted on close.
//   field(label, name, attrs)       — DOM helper: a `<label>` wrapping an input.
//   buildCreateWorldForm(ctl, err)  — name/setting/description/grid form; on
//       submit calls createWorld() and ctl.choose({ kind:'build', ... }).
//   buildJoinForm(ctl, err)         — invite-code form; ctl.choose({ kind:'play' }).
//   buildCreatePartyForm(world, …)  — party-name form scoped to one world.

import { createParty, createWorld, joinParty, type WorldSummary } from '../../api.js';
import type { HomeChoice } from '../title.js';

export interface DrawerCtl { choose: (c: HomeChoice) => void; signal: AbortSignal; }

export function field(label: string, name: string, attrs: Record<string, string> = {}): HTMLLabelElement {
  const l = document.createElement('label');
  l.textContent = label + ' ';
  const i = document.createElement('input');
  i.name = name;
  for (const [k, v] of Object.entries(attrs)) i.setAttribute(k, v);
  l.appendChild(i);
  return l;
}

export function buildCreateWorldForm(ctl: DrawerCtl, error: HTMLElement): HTMLFormElement {
  const form = document.createElement('form');
  form.appendChild(field('Name', 'name', { required: 'true' }));
  form.appendChild(field('Setting', 'setting', { placeholder: 'fantasy / sci-fi / weird west…', required: 'true' }));
  form.appendChild(field('Description', 'description', { placeholder: '(optional)' }));
  const dims = document.createElement('div');
  dims.style.cssText = 'display:flex; gap:8px;';
  const wl = field('Width', 'grid_width', { type: 'number', min: '3', max: '64', value: '9', required: 'true' });
  const hl = field('Height', 'grid_height', { type: 'number', min: '3', max: '64', value: '7', required: 'true' });
  wl.style.flex = hl.style.flex = '1';
  dims.append(wl, hl);
  form.appendChild(dims);
  const btn = document.createElement('button');
  btn.type = 'submit'; btn.className = 'primary'; btn.textContent = 'Create & build';
  form.appendChild(btn);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    const fd = new FormData(form);
    try {
      const { world_id } = await createWorld({
        name: String(fd.get('name')),
        setting: String(fd.get('setting')),
        description: String(fd.get('description') ?? ''),
        grid_width: Number(fd.get('grid_width')),
        grid_height: Number(fd.get('grid_height')),
      });
      ctl.choose({ kind: 'build', worldId: world_id });
    } catch (err) {
      error.textContent = (err as Error).message;
    }
  }, { signal: ctl.signal });
  return form;
}

export function buildJoinForm(ctl: DrawerCtl, error: HTMLElement): HTMLFormElement {
  const form = document.createElement('form');
  form.style.cssText = 'flex-direction:row; align-items:flex-end; gap:8px;';
  const codeLabel = field('Invite code', 'invite_code', { autocomplete: 'off' });
  codeLabel.style.flex = '1';
  form.appendChild(codeLabel);
  const btn = document.createElement('button');
  btn.type = 'submit'; btn.className = 'primary'; btn.textContent = 'Join';
  form.appendChild(btn);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    const code = String(new FormData(form).get('invite_code') ?? '').trim();
    if (!code) return;
    try {
      const { party_id } = await joinParty(code);
      ctl.choose({ kind: 'play', partyId: party_id });
    } catch (err) {
      error.textContent = (err as Error).message;
    }
  }, { signal: ctl.signal });
  return form;
}

export function buildCreatePartyForm(world: WorldSummary, ctl: DrawerCtl, error: HTMLElement): HTMLFormElement {
  const form = document.createElement('form');
  const sub = document.createElement('p');
  sub.className = 'party-meta';
  sub.textContent = `New party in: ${world.name} (${world.setting})`;
  form.appendChild(sub);
  form.appendChild(field('Party name', 'name', { required: 'true' }));
  const btn = document.createElement('button');
  btn.type = 'submit'; btn.className = 'primary'; btn.textContent = 'Create party';
  form.appendChild(btn);
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    const name = String(new FormData(form).get('name') ?? '').trim();
    if (!name) return;
    try {
      const { party_id } = await createParty(world.id, name);
      ctl.choose({ kind: 'play', partyId: party_id });
    } catch (err) {
      error.textContent = (err as Error).message;
    }
  }, { signal: ctl.signal });
  return form;
}
